// -----------------------------------------------
// taskExitAnimation — shared keyframes + broadcast helper for the V2 task
// list per-card exit animation (#sidebar-v2 Phase 2) + the Phase 3 Undo
// Snackbar restore pipeline.
//
// Phase 2: when a checklist item completion causes the parent task to
// transition to ``status === 'completed'``, the broadcaster (TaskBodyView)
// dispatches a ``tasks-v2:task-completing`` CustomEvent on ``window``
// carrying the task id and the animation duration (ms). Each list view
// (TaskBoardView, TaskColumnView, LeadTaskList, DashboardTaskListV2)
// listens for the event, flags the matching row's local-state entry as
// ``_isExiting``, and lets ``AnimatePresence`` play the ``exit`` variant
// before the row is dropped from local state.
//
// Phase 3 (this module addition): the broadcast can ALSO carry a
// ``taskSnapshot`` + ``lastItemId`` payload that is stashed in-memory
// against a per-completion token. ``TaskUndoSnackbarHost`` listens for
// the same event, shows a Snackbar with an Undo action, and on click
// dispatches a ``tasks-v2:task-restore`` event that the list views
// react to by re-inserting the stashed task object — visually reversing
// the exit. The BE-side reopen is handled by the Snackbar host itself.
//
// Centralising the variants + duration here guarantees every list view
// uses the exact same iOS-style easing curve (#244 design spec).
// -----------------------------------------------

export const TASK_EXIT_EVENT = 'tasks-v2:task-completing';
export const TASK_RESTORE_EVENT = 'tasks-v2:task-restore';

// 200ms matches the design spec (#244). If you change this, also bump the
// post-exit local-state drop timeout in each list view by the same delta.
export const TASK_EXIT_MS = 200;

// 6-second undo window per Phase 3 spec.
export const UNDO_WINDOW_MS = 6000;

export const TASK_EXIT_VARIANTS = {
    initial: { opacity: 1, height: 'auto', x: 0 },
    animate: { opacity: 1, height: 'auto', x: 0 },
    exit: {
        opacity: 0,
        height: 0,
        x: 24,
        transition: { duration: 0.2, ease: [0.32, 0.72, 0, 1] },
    },
};

// -----------------------------------------------
// Phase 3 Undo stash
// -----------------------------------------------
// Token-keyed in-memory map. Lifetime is bounded by the Undo window —
// every entry self-expires shortly after UNDO_WINDOW_MS so a forgotten
// completion can't leak the snapshot indefinitely. ``popUndo`` drains the
// entry on read so only ONE consumer fires the undo even when multiple
// Snackbar hosts (Lead detail + Dashboard) are mounted at once.
//
// Tech-debt (P2-2 I2): the previous self-expiry setTimeout fired even
// after ``popUndo`` had already drained the entry, and there was no upper
// bound on stash size so a navigation away mid-window (or a burst of
// completions) could pile up snapshots indefinitely. We now (a) cap the
// stash at MAX_STASH_ENTRIES, dropping the oldest insertion when full,
// and (b) store the expiry timer id alongside the payload so ``popUndo``
// can clear it, preventing the orphan-callback leak.
const MAX_STASH_ENTRIES = 20;
const undoStash = new Map();

export function stashForUndo(token, payload) {
    if (!token) return;
    // Drop the oldest entry first when at capacity. ``Map`` preserves
    // insertion order, so ``keys().next().value`` is the oldest token.
    if (undoStash.size >= MAX_STASH_ENTRIES) {
        const oldestToken = undoStash.keys().next().value;
        const oldest = undoStash.get(oldestToken);
        if (oldest?.timerId) clearTimeout(oldest.timerId);
        undoStash.delete(oldestToken);
    }
    const timerId = setTimeout(() => undoStash.delete(token), UNDO_WINDOW_MS + 500);
    undoStash.set(token, { payload, timerId });
}

export function popUndo(token) {
    if (!token) return null;
    const entry = undoStash.get(token);
    undoStash.delete(token);
    if (!entry) return null;
    if (entry.timerId) clearTimeout(entry.timerId);
    return entry.payload || null;
}

/**
 * Broadcast a "task is about to complete + exit" signal to all V2 list
 * views currently mounted. Safe no-op if ``taskId`` is missing or if we're
 * called in a non-browser context (SSR/test harness without ``window``).
 *
 * Phase 3: optional ``payload.taskSnapshot`` + ``payload.lastItemId`` are
 * stashed against a generated token. The token is included in the event
 * detail so the Snackbar host can later resolve the snapshot.
 *
 * @param {string|number} taskId
 * @param {number} [animationMs]
 * @param {{ taskSnapshot?: object, lastItemId?: string|number }} [payload]
 * @returns {string|undefined} token (only when a snapshot was stashed)
 */
export function broadcastTaskCompleting(taskId, animationMs = TASK_EXIT_MS, payload = {}) {
    if (!taskId) return undefined;
    if (typeof window === 'undefined') return undefined;
    // Token format is in-memory-only keying for the undoStash Map —
    // never persisted, never sent to the BE, so collision resistance just
    // needs to beat concurrent completions in a single browser tab.
    const token = `${taskId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const hasUndoPayload = payload && payload.taskSnapshot && payload.lastItemId;
    if (hasUndoPayload) {
        stashForUndo(token, {
            taskSnapshot: payload.taskSnapshot,
            lastItemId: payload.lastItemId,
        });
    }
    window.dispatchEvent(new CustomEvent(TASK_EXIT_EVENT, {
        detail: {
            taskId: String(taskId),
            animationMs,
            token: hasUndoPayload ? token : null,
        },
    }));
    return hasUndoPayload ? token : undefined;
}

/**
 * Broadcast a "restore this task back into the lists" signal — the
 * inverse of TASK_EXIT_EVENT. List views listen and re-insert the
 * supplied task object so AnimatePresence plays its enter animation
 * automatically.
 *
 * @param {string} token
 * @param {object} task
 */
export function broadcastTaskRestore(token, task) {
    if (!task?.id) return;
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(TASK_RESTORE_EVENT, {
        detail: { token, task },
    }));
}
